/* eslint-disable camelcase */
import { Module } from 'vuex'
import { RootState } from '../types'
import api from '@/api/axios'

export interface LeaseRecord {
  id: number
  unit_id: number
  unit_name: string
  property_id: number
  property_name: string
  property_address: string
  rent_amount: number
  lease_start: string
  lease_end: string | null
  status: 'active' | 'completed' | 'terminated'
  total_invoiced: number
  total_paid: number
  outstanding: number
}

export interface TenantSummary {
  tenant_id: number
  tenant_name: string
  total_leases: number
  active_lease: boolean
  total_invoiced: number
  total_paid: number
  outstanding_balance: number
  overdue_invoices: number
  on_time_payment_rate: number
  first_lease_date: string
  months_as_tenant: number
}

export interface OccupancyRecord {
  unit_id: number
  unit_name: string
  unit_type: string
  rent_amount: number
  tenant_id: number
  tenant_name: string
  tenant_phone: string
  lease_start: string
  lease_end: string | null
  status: 'active' | 'completed' | 'terminated'
  duration_months: number
  total_paid: number
}

export interface RevenueMonth {
  month: string
  month_label: string
  expected_income: number
  collected_income: number
  expenses: number
  net_profit: number
  collection_rate: number
  invoices_count: number
  paid_invoices_count: number
  overdue_invoices_count: number
}

export interface PropertySummary {
  property_id: number
  property_name: string
  total_units: number
  total_tenants_ever: number
  current_tenants: number
  average_occupancy_rate: number
  total_revenue_all_time: number
  total_expenses_all_time: number
  total_profit_all_time: number
  average_monthly_revenue: number
  average_lease_duration_months: number
  longest_lease_months: number
  vacancy_rate: number
}

export interface HistoryState {
  tenantLeases: LeaseRecord[]
  tenantSummary: TenantSummary | null
  occupancyHistory: OccupancyRecord[]
  revenueHistory: RevenueMonth[]
  propertySummary: PropertySummary | null
  loading: boolean
}

const history: Module<HistoryState, RootState> = {
  namespaced: true,

  state: (): HistoryState => ({
    tenantLeases: [],
    tenantSummary: null,
    occupancyHistory: [],
    revenueHistory: [],
    propertySummary: null,
    loading: false
  }),

  getters: {
    tenantLeases: (state) => state.tenantLeases,
    tenantSummary: (state) => state.tenantSummary,
    occupancyHistory: (state) => state.occupancyHistory,
    revenueHistory: (state) => state.revenueHistory,
    propertySummary: (state) => state.propertySummary,
    loading: (state) => state.loading
  },

  mutations: {
    SET_TENANT_LEASES (state, leases: LeaseRecord[]) { state.tenantLeases = leases },
    SET_TENANT_SUMMARY (state, summary: TenantSummary) { state.tenantSummary = summary },
    SET_OCCUPANCY (state, records: OccupancyRecord[]) { state.occupancyHistory = records },
    SET_REVENUE (state, months: RevenueMonth[]) { state.revenueHistory = months },
    SET_PROPERTY_SUMMARY (state, summary: PropertySummary) { state.propertySummary = summary },
    SET_LOADING (state, val: boolean) { state.loading = val },
    RESET_TENANT (state) {
      state.tenantLeases = []
      state.tenantSummary = null
    },
    RESET_PROPERTY (state) {
      state.occupancyHistory = []
      state.revenueHistory = []
      state.propertySummary = null
    }
  },

  actions: {
    async fetchTenantHistory ({ commit }, tenantId: number) {
      commit('SET_LOADING', true)
      commit('RESET_TENANT')
      try {
        const [leasesRes, summaryRes] = await Promise.all([
          api.get(`/tenants/${tenantId}/leases`),
          api.get(`/tenants/${tenantId}/summary`)
        ])
        commit('SET_TENANT_LEASES', leasesRes.data.data ?? leasesRes.data)
        commit('SET_TENANT_SUMMARY', summaryRes.data.data ?? summaryRes.data)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchPropertyHistory ({ commit }, propertyId: number) {
      commit('SET_LOADING', true)
      commit('RESET_PROPERTY')
      try {
        const [occupancyRes, revenueRes, summaryRes] = await Promise.all([
          api.get(`/properties/${propertyId}/occupancy-history`),
          api.get(`/properties/${propertyId}/revenue-history`),
          api.get(`/properties/${propertyId}/summary`)
        ])
        commit('SET_OCCUPANCY', occupancyRes.data.data ?? occupancyRes.data)
        commit('SET_REVENUE', revenueRes.data.data ?? revenueRes.data)
        commit('SET_PROPERTY_SUMMARY', summaryRes.data.data ?? summaryRes.data)
      } finally {
        commit('SET_LOADING', false)
      }
    }
  }
}

export default history
